import { useEffect, useState, useRef } from "react";
import { Check } from "lucide-react";

/**
 * Center-of-screen "Added to cart" confirmation.
 *
 * Fired via a lightweight event bus (no context / no re-render of tree).
 * A single overlay is mounted once in <RootComponent>.
 */

type Payload = { message: string; description?: string };
const EVENT = "cart:added-toast";

export function showAddedToCartToast(payload: Payload = { message: "Added to cart" }) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<Payload>(EVENT, { detail: payload }));
}

export function AddedToCartToast() {
  const [visible, setVisible] = useState(false);
  const [payload, setPayload] = useState<Payload | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const p = (e as CustomEvent<Payload>).detail ?? { message: "Added to cart" };
      if (hideTimer.current) clearTimeout(hideTimer.current);
      if (clearTimer.current) clearTimeout(clearTimer.current);
      setPayload(p);
      // next frame so enter animation always plays
      requestAnimationFrame(() => setVisible(true));
      // Light haptic feedback on supported devices
      if (typeof navigator !== "undefined" && "vibrate" in navigator) {
        try { (navigator as any).vibrate?.(15); } catch { /* noop */ }
      }
      hideTimer.current = setTimeout(() => setVisible(false), 1100);
      clearTimer.current = setTimeout(() => setPayload(null), 1400);
    };
    window.addEventListener(EVENT, handler);
    return () => {
      window.removeEventListener(EVENT, handler);
      if (hideTimer.current) clearTimeout(hideTimer.current);
      if (clearTimer.current) clearTimeout(clearTimer.current);
    };
  }, []);

  if (!payload) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-0 z-[100] grid place-items-center"
    >
      <div
        className={[
          "flex flex-col items-center gap-2.5 rounded-2xl bg-white/95 backdrop-blur",
          "px-6 py-5 shadow-[0_20px_60px_-15px_rgba(15,23,42,0.35)] border border-slate-200/80",
          "transition-all duration-300 ease-out will-change-transform",
          visible ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-90 translate-y-1",
        ].join(" ")}
        style={{ minWidth: 160 }}
      >
        <span
          className={[
            "relative grid place-items-center h-14 w-14 rounded-full",
            "bg-emerald-500 text-white shadow-md",
            visible ? "animate-[cart-pop_400ms_cubic-bezier(0.34,1.56,0.64,1)_both]" : "",
          ].join(" ")}
          aria-hidden="true"
        >
          {/* Soft glow ring */}
          <span
            className={[
              "absolute inset-0 rounded-full bg-emerald-400/40",
              visible ? "animate-[cart-ring_600ms_ease-out_both]" : "",
            ].join(" ")}
          />
          <Check className="relative h-7 w-7 stroke-[3]" />
        </span>
        <div className="text-center">
          <div className="text-sm font-semibold text-[#172554]">{payload.message}</div>
          {payload.description ? (
            <div className="mt-0.5 max-w-[220px] truncate text-[11px] text-slate-500">
              {payload.description}
            </div>
          ) : null}
        </div>
      </div>

      {/* Keyframes — kept scoped here so we don't touch global styles */}
      <style>{`
        @keyframes cart-pop {
          0%   { transform: scale(0.4); opacity: 0; }
          60%  { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1);    opacity: 1; }
        }
        @keyframes cart-ring {
          0%   { transform: scale(0.6); opacity: 0.6; }
          100% { transform: scale(1.8); opacity: 0;   }
        }
      `}</style>
    </div>
  );
}
